<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import useSettingStore from '../../stores/setting';
import useLocale from '../../composables/useLocale';
import useMediaStore from '../../stores/media';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { FontAwesomeIconSizes } from '../../types';
import useReaderStore from '../../stores/reader';
import { useDisplay } from 'vuetify';

type Props = {
  isControlsVisible?: boolean;
  iconSize: FontAwesomeIconSizes;
  btnSize?: number | string;
  showTooltips?: boolean;
};
withDefaults(defineProps<Props>(), {
  isControlsVisible: false,
  iconSize: 'xl',
  btnSize: 48,
  showTooltips: true,
});

const { t } = useLocale();
const { hasMediaAvailable } = storeToRefs(useMediaStore());
const { muted, isMuted, audioVolume } = storeToRefs(useSettingStore());
const { isAudioOnly } = storeToRefs(useReaderStore());
const { smAndDown, sm } = useDisplay();

const iconName = computed(() => {
  switch (true) {
    case !hasMediaAvailable.value || audioVolume.value === 0.0 || isMuted.value:
      return isAudioOnly.value ? 'far fa-volume-slash' : 'fa-solid fa-volume-slash';
    case audioVolume.value < 0.3:
      return isAudioOnly.value ? 'far fa-volume-low' : 'fa-solid fa-volume-low';
    case audioVolume.value > 0.7:
      return isAudioOnly.value ? 'far fa-volume-high' : 'fa-solid fa-volume-high';
    default:
      return isAudioOnly.value ? 'far fa-volume' : 'fa-solid fa-volume';
  }
});
</script>

<template>
  <v-btn
    id="audio-volume-btn"
    data-testid="bp-audio-volume-btn"
    color="on-primary"
    :title="t(isMuted ? 'button_volume_muted' : 'button_volume')"
    :disabled="!hasMediaAvailable"
    v-bind="{ ...$attrs }"
    :icon="!isAudioOnly"
    variant="text"
    :ripple="false"
    :aria-owns="isControlsVisible ? 'volume-control' : null"
    @click.stop="muted = !muted"
    :height="btnSize"
    :width="isAudioOnly && !smAndDown ? '190' : isAudioOnly && sm ? '120' : btnSize"
    :slim="isAudioOnly && smAndDown"
  >
    <div :class="{ 'd-flex flex-column flex-md-row align-center ga-2 overflow-hidden': isAudioOnly }">
      <FontAwesomeIcon :size="iconSize" :icon="iconName"></FontAwesomeIcon>
      <span v-if="isAudioOnly" class="text-truncate" :class="smAndDown ? 'text-body-2 w-100' : 'text-body-1'">{{
        t('book_player.button_volume')
      }}</span>
    </div>
    <v-tooltip
      :disabled="!showTooltips"
      activator="parent"
      location="bottom"
      :text="t(isMuted ? 'button_volume' : 'button_volume_muted')"
    />
  </v-btn>
</template>

<style scoped lang="scss"></style>
